import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { blockService } from '../services/blockService';
import { useNotification } from './NotificationContext';

interface BlockUserDialogProps {
  open: boolean;
  userId: string;
  displayName: string;
  onClose: () => void;
  onBlocked: () => void;
}

/** Confirms a block and says plainly what it does — it's easy to hit by accident and hard to notice later. */
const BlockUserDialog: React.FC<BlockUserDialogProps> = ({ open, userId, displayName, onClose, onBlocked }) => {
  const { showSuccess, showError } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await blockService.block(userId);
      showSuccess(`Blocked ${displayName}`);
      onBlocked();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to block user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Block {displayName}?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>You won't see each other's posts, comments, or profiles.</li>
            <li>Neither of you can message, follow, or send buddy requests to the other.</li>
            <li>Any existing buddy or follow connection is removed.</li>
            <li>{displayName} isn't notified. You can unblock them any time in Settings → Privacy.</li>
          </ul>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : 'Block'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockUserDialog;
